import React, { useState, useRef, useEffect } from 'react';
import './DualRangeSlider.css';

interface DualRangeSliderProps {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue?: (value: number) => string;
  className?: string;
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  step,
  value,
  onChange,
  formatValue = (v) => `$${v.toLocaleString()}`,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);
  const [tempMinValue, setTempMinValue] = useState('');
  const [tempMaxValue, setTempMaxValue] = useState('');
  const sliderRef = useRef<HTMLDivElement>(null);
  const [minValue, maxValue] = value;

  // Calculate percentages for positioning
  const minPercent = ((minValue - min) / (max - min)) * 100;
  const maxPercent = ((maxValue - min) / (max - min)) * 100;

  // Handle mouse/touch events
  const handleMouseDown = (type: 'min' | 'max') => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(type);
  };

  const handleMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const percent = Math.min(100, Math.max(0, ((x - rect.left) / rect.width) * 100));
    const newValue = Math.round((percent / 100) * (max - min) / step) * step + min;

    if (isDragging === 'min') {
      const newMin = Math.min(newValue, maxValue - step);
      onChange([newMin, maxValue]);
    } else {
      const newMax = Math.max(newValue, minValue + step);
      onChange([minValue, newMax]);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  // Add global event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, minValue, maxValue]);

  // Handle clicking on the track
  const handleTrackClick = (e: React.MouseEvent) => {
    if (!sliderRef.current || isDragging) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const percent = ((e.clientX - rect.left) / rect.width) * 100;
    const clickValue = Math.round((percent / 100) * (max - min) / step) * step + min;

    // Determine which thumb to move based on proximity
    const minDistance = Math.abs(clickValue - minValue);
    const maxDistance = Math.abs(clickValue - maxValue);

    if (minDistance < maxDistance && clickValue < maxValue) {
      onChange([clickValue, maxValue]);
    } else if (clickValue > minValue) {
      onChange([minValue, clickValue]);
    }
  };

  // Handle editing of values
  const handleMinEdit = () => {
    setIsEditingMin(true);
    setTempMinValue(minValue.toString());
  };

  const handleMaxEdit = () => {
    setIsEditingMax(true);
    setTempMaxValue(maxValue.toString());
  };

  const handleMinBlur = () => {
    const newMin = parseInt(tempMinValue) || minValue;
    const validMin = Math.max(min, Math.min(newMin, maxValue - step));
    onChange([validMin, maxValue]);
    setIsEditingMin(false);
  };

  const handleMaxBlur = () => {
    const newMax = parseInt(tempMaxValue) || maxValue;
    const validMax = Math.min(max, Math.max(newMax, minValue + step));
    onChange([minValue, validMax]);
    setIsEditingMax(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, type: 'min' | 'max') => {
    if (e.key === 'Enter') {
      if (type === 'min') {
        handleMinBlur();
      } else {
        handleMaxBlur();
      }
    }
  };

  return (
    <div className={`dual-range-slider-container ${className}`}>
      <div className="slider-values">
        {isEditingMin ? (
          <input
            type="number"
            value={tempMinValue}
            onChange={(e) => setTempMinValue(e.target.value)}
            onBlur={handleMinBlur}
            onKeyPress={(e) => handleKeyPress(e, 'min')}
            className="slider-value-input"
            autoFocus
          />
        ) : (
          <span className="slider-value clickable" onClick={handleMinEdit}>
            {formatValue(minValue)}
          </span>
        )}
        {isEditingMax ? (
          <input
            type="number"
            value={tempMaxValue}
            onChange={(e) => setTempMaxValue(e.target.value)}
            onBlur={handleMaxBlur}
            onKeyPress={(e) => handleKeyPress(e, 'max')}
            className="slider-value-input"
            autoFocus
          />
        ) : (
          <span className="slider-value clickable" onClick={handleMaxEdit}>
            {formatValue(maxValue)}
          </span>
        )}
      </div>
      
      <div 
        ref={sliderRef}
        className="slider-wrapper"
        onClick={handleTrackClick}
      >
        <div className="slider-track" />
        <div 
          className="slider-range"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`
          }}
        />
        
        {/* Min thumb */}
        <div
          className={`slider-thumb thumb-min ${isDragging === 'min' ? 'dragging' : ''}`}
          style={{ left: `${minPercent}%` }}
          onMouseDown={handleMouseDown('min')}
          onTouchStart={handleMouseDown('min')}
          role="slider"
          aria-label="Minimum value"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={minValue}
          tabIndex={0}
        >
          <div className="thumb-tooltip">{formatValue(minValue)}</div>
        </div>
        
        {/* Max thumb */}
        <div
          className={`slider-thumb thumb-max ${isDragging === 'max' ? 'dragging' : ''}`}
          style={{ left: `${maxPercent}%` }}
          onMouseDown={handleMouseDown('max')}
          onTouchStart={handleMouseDown('max')}
          role="slider"
          aria-label="Maximum value"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={maxValue}
          tabIndex={0}
        >
          <div className="thumb-tooltip">{formatValue(maxValue)}</div>
        </div>
      </div>
    </div>
  );
};

export default DualRangeSlider;